import React, { useMemo } from "react";

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function ButterflyInlineLoader({
  animate = true,
  piecesBasePath = "/pieces",
  durationMs = 2600,
  staggerMs = 160,
  maxOffsetPx = 150,
  maxRotateDeg = 24,
  seed = 42,
  sizePx = 220,
  floatAfterAssemble = true,
  style,
}) {
  const pieceFilenames = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => `piece_${String(i + 1).padStart(2, "0")}.png`),
    []
  );

  const pieceStarts = useMemo(() => {
    const rand = mulberry32(seed);
    return pieceFilenames.map(() => {
      const angle = rand() * Math.PI * 2;
      const r = (0.35 + rand() * 0.65) * maxOffsetPx;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      const rot = (rand() * 2 - 1) * maxRotateDeg;
      const blur = 8 + rand() * 10;
      const scale = 0.92 + rand() * 0.12;
      return { x, y, rot, blur, scale };
    });
  }, [pieceFilenames, seed, maxOffsetPx, maxRotateDeg]);

  const totalAssembleMs = durationMs + staggerMs * (pieceFilenames.length - 1);

  return (
    <div style={{ display: "grid", placeItems: "center", ...style }}>
      <style>{`
        @keyframes butterflyPieceIn {
          0% {
            opacity: 0;
            transform: translate(var(--x), var(--y)) rotate(var(--r)) scale(var(--s));
            filter: blur(var(--b));
          }
          55% { opacity: 1; }
          100% {
            opacity: 1;
            transform: translate(0px, 0px) rotate(0deg) scale(1);
            filter: blur(0px);
          }
        }

        @keyframes butterflyFloat {
          0%   { transform: translateY(0px) scale(1); }
          50%  { transform: translateY(-6px) scale(1.01); }
          100% { transform: translateY(0px) scale(1); }
        }

        @keyframes butterflyPulse {
          0%   { opacity: 1; }
          50%  { opacity: 0.96; }
          100% { opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .butterfly-piece { animation: none !important; opacity: 1 !important; transform: none !important; filter: none !important; }
          .butterfly-inline-float { animation: none !important; }
        }
      `}</style>

      <div
        className="butterfly-inline-float"
        style={{
          width: sizePx,
          height: sizePx,
          position: "relative",

          // Float only after assemble finishes
          animationName:
            animate && floatAfterAssemble ? "butterflyFloat, butterflyPulse" : "none",
          animationDuration: animate && floatAfterAssemble ? "1600ms, 2200ms" : undefined,
          animationTimingFunction: animate && floatAfterAssemble ? "ease-in-out, ease-in-out" : undefined,
          animationIterationCount: animate && floatAfterAssemble ? "infinite, infinite" : undefined,
          animationDelay: animate && floatAfterAssemble ? `${totalAssembleMs}ms, ${totalAssembleMs}ms` : undefined,
        }}
      >
        {pieceFilenames.map((file, i) => {
          const start = pieceStarts[i];

          // If animate is false, show assembled (no animation / no offset)
          const animName = animate ? "butterflyPieceIn" : "none";
          const delay = animate ? `${i * staggerMs}ms` : "0ms";

          return (
            <img
              key={file}
              src={`${piecesBasePath}/${file}`}
              alt=""
              draggable={false}
              className="butterfly-piece"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                pointerEvents: "none",
                background: "transparent",
                display: "block",

                ["--x"]: `${start.x.toFixed(2)}px`,
                ["--y"]: `${start.y.toFixed(2)}px`,
                ["--r"]: `${start.rot.toFixed(2)}deg`,
                ["--b"]: `${start.blur.toFixed(2)}px`,
                ["--s"]: `${start.scale.toFixed(3)}`,

                animationName: animName,
                animationDuration: `${durationMs}ms`,
                animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
                animationFillMode: "both",
                animationDelay: delay,

                // When animate is false, force final state
                opacity: animate ? undefined : 1,
                filter: animate ? undefined : "none",
                transform: animate ? undefined : "none",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

// Export for backwards compatibility
export { ButterflyInlineLoader as ButterflyLoader };
